import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import * as Sentry from '@sentry/react';
import React, { Component, type ErrorInfo, type FC, useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import config from '../../config/config';
import {
  CHUNK_REFRESH_KEY,
  getStorageItem,
  isChunkLoadError,
  setStorageItem,
} from '../../Utils/lazyWithRetry';

const styles: Record<string, React.CSSProperties> = {
  errorCol: {
    justifyContent: 'center',
    paddingTop: '5em',
  },
  h3: {
    textAlign: 'center' as const,
  },
  btnError: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  iconError: {
    fontSize: '9em',
    color: 'red',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1rem',
  },
  btnReturn: {
    border: '1px solid #FAF0E6',
    color: '#F36A00',
    backgroundColor: '#FCF7F2',
    borderRadius: '8px',
    fontSize: '16px',
    marginRight: '1.5rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    textTransform: 'none' as const,
    fontFamily: 'IBM Plex Sans Condensed, sans-serif',
  },
  btnNext: {
    border: '1px solid #FF8201',
    color: '#FFF',
    backgroundColor: '#FF8201',
    borderRadius: '8px',
    fontSize: '16px',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    textTransform: 'none' as const,
    fontFamily: 'IBM Plex Sans Condensed, sans-serif',
  },
};

export const ErrorFallback: FC<{ eventId?: string }> = ({ eventId }) => {
  const reloadPage = () => {
    window.location.href = config.myURI!;
  };

  const showReportDialog = () => {
    Sentry.showReportDialog({ eventId });
  };

  return (
    <Grid container style={styles.errorCol}>
      <Grid size={{ xs: 12, sm: 10, md: 8, lg: 6 }}>
        <div style={styles.iconError}>
          <i className="fa fa-exclamation-triangle" aria-hidden="true" />
        </div>
        <h3 style={styles.h3}>Digidom est vraiment désolé pour ce désagrément.</h3>
        <div style={styles.btnError}>
          <Button variant="outlined" style={styles.btnReturn} onClick={reloadPage}>
            Retour à la page d'accueil
          </Button>
          <Button variant="outlined" style={styles.btnNext} onClick={showReportDialog}>
            Reporter l'erreur
          </Button>
        </div>
      </Grid>
    </Grid>
  );
};

export const RouteErrorBoundary: FC = () => {
  const error = useRouteError();
  const [eventId, setEventId] = useState<string | undefined>();
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    if (error) {
      if (isChunkError) {
        const pageAlreadyRefreshed = getStorageItem(CHUNK_REFRESH_KEY) === 'true';
        if (!pageAlreadyRefreshed) {
          setStorageItem(CHUNK_REFRESH_KEY, 'true');
          if (typeof window !== 'undefined' && window.location) {
            window.location.reload();
          }
          return;
        }
      }

      if (config.nodeENV !== 'local') {
        const id = Sentry.captureException(error);
        setEventId(id);
      }
    }
  }, [error, isChunkError]);

  if (isChunkError) {
    return (
      <Grid container style={styles.errorCol}>
        <Grid size={{ xs: 12, sm: 10, md: 8, lg: 6 }}>
          <h3 style={styles.h3}>Mise à jour disponible, rechargement en cours...</h3>
        </Grid>
      </Grid>
    );
  }

  return <ErrorFallback eventId={eventId} />;
};

interface ErrorBoundaryProps {
  children: React.JSX.Element;
}

interface ErrorBoundaryState {
  eventId?: string;
  hasError: boolean;
  isChunkError?: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { eventId: undefined, hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      const pageAlreadyRefreshed = getStorageItem(CHUNK_REFRESH_KEY) === 'true';
      if (!pageAlreadyRefreshed) {
        setStorageItem(CHUNK_REFRESH_KEY, 'true');
        if (typeof window !== 'undefined' && window.location) {
          window.location.reload();
        }
        return;
      }
    }

    if (config.nodeENV !== 'local') {
      Sentry.withScope((scope) => {
        scope.setExtras(errorInfo as unknown as Record<string, unknown>);
        const eventId = Sentry.captureException(error);
        this.setState({ eventId, hasError: true });
      });
    } else {
      this.setState({ hasError: true });
    }
  }

  render() {
    const { hasError, eventId, isChunkError } = this.state;
    const { children } = this.props;
    if (hasError) {
      if (isChunkError) {
        return (
          <Grid container style={styles.errorCol}>
            <Grid size={{ xs: 12, sm: 10, md: 8, lg: 6 }}>
              <h3 style={styles.h3}>Mise à jour disponible, rechargement en cours...</h3>
            </Grid>
          </Grid>
        );
      }
      return <ErrorFallback eventId={eventId} />;
    }

    return children;
  }
}

export default ErrorBoundary;
